import { Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import Cropper from 'cropperjs';
import { PROFILE_COLOR_PALETTE, DEFAULT_PROFILE_COLOR } from '@newgenus/common';
import { ColorPickerModule } from 'ngx-color-picker';

export interface ProfilePhotoUploadDialogData {
    currentPhotoUrl?: string;
    userId: string;
    /** Current profile placeholder color */
    currentProfileColor?: string;
    /** User's initials for preview */
    userInitials?: string;
}

export interface ProfilePhotoUploadDialogResult {
    action: 'upload' | 'remove' | 'cancel' | 'color';
    croppedImageBlob?: Blob;
    /** Selected profile color (when action is 'color') */
    profileColor?: string;
}

@Component({
    selector: 'newgenus-profile-photo-upload-dialog',
    templateUrl: './profile-photo-upload-dialog.component.html',
    styleUrls: ['./profile-photo-upload-dialog.component.scss'],
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSliderModule,
        MatTooltipModule,
        ColorPickerModule,
    ],
})
export class ProfilePhotoUploadDialogComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('imageElement') imageElement!: ElementRef<HTMLImageElement>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    public cropper: Cropper | null = null;
    public imageLoaded = false;
    public isLoading = false;
    public isDragging = false;
    public imageSrc: string | null = null;
    public zoomValue = 0;
    public hasExistingPhoto = false;

    /** Color picker state */
    public colorPalette = PROFILE_COLOR_PALETTE;
    public selectedColor: string;
    public userInitials: string;
    /** Whether the currently selected color is a custom (non-palette) color */
    public isCustomColor = false;
    /** Custom colors added by the user to the palette */
    public customColors: string[] = [];
    /** Temporary color for the ngx-color-picker (live preview) */
    public cpColor: string;
    /** Whether the custom color picker popup is open */
    public cpOpen = false;

    constructor(
        public dialogRef: MatDialogRef<ProfilePhotoUploadDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ProfilePhotoUploadDialogData
    ) {
        this.hasExistingPhoto = !!data.currentPhotoUrl;
        this.selectedColor = data.currentProfileColor || DEFAULT_PROFILE_COLOR;
        this.userInitials = data.userInitials || '?';
        this.isCustomColor = !PROFILE_COLOR_PALETTE.includes(this.selectedColor);
        this.cpColor = this.selectedColor;
    }

    public ngOnInit(): void {
        // Component initialization - cropper is initialized after image loads
        console.log('ProfilePhotoUploadDialog initialized');
    }

    public ngAfterViewInit(): void {
        // View initialized - cropper will be set up when image is selected
        console.log('ProfilePhotoUploadDialog view initialized');
    }

    public ngOnDestroy(): void {
        this.destroyCropper();
    }

    /**
     * Handle file input change
     */
    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.loadImage(input.files[0]);
        }
    }

    /**
     * Handle drag over
     */
    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
    }

    /**
     * Handle drag leave
     */
    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
    }

    /**
     * Handle drop
     */
    public onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;

        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            const file = event.dataTransfer.files[0];
            if (file.type.startsWith('image/')) {
                this.loadImage(file);
            }
        }
    }

    /**
     * Load image from file
     */
    private loadImage(file: File): void {
        this.isLoading = true;
        const reader = new FileReader();

        reader.onload = (e: ProgressEvent<FileReader>) => {
            this.imageSrc = e.target?.result as string;
            this.isLoading = false;
            // Initialize cropper after image loads
            setTimeout(() => this.initializeCropper(), 100);
        };

        reader.onerror = () => {
            this.isLoading = false;
            console.error('Failed to load image');
        };

        reader.readAsDataURL(file);
    }

    /**
     * Initialize Cropper.js
     */
    private initializeCropper(): void {
        this.destroyCropper();

        if (this.imageElement?.nativeElement) {
            this.cropper = new Cropper(this.imageElement.nativeElement, {
                aspectRatio: 1, // Square crop for profile photo
                viewMode: 0, // Allow free movement of canvas (repositioning)
                dragMode: 'move',
                autoCropArea: 0.9,
                restore: false,
                guides: true,
                center: true,
                highlight: false,
                cropBoxMovable: false, // Keep crop box fixed, move image instead
                cropBoxResizable: false, // Keep crop box fixed size
                toggleDragModeOnDblclick: false,
                ready: () => {
                    this.imageLoaded = true;
                },
            });
        }
    }

    /**
     * Destroy cropper instance
     */
    private destroyCropper(): void {
        if (this.cropper) {
            this.cropper.destroy();
            this.cropper = null;
        }
        this.imageLoaded = false;
    }

    /**
     * Trigger file input click
     */
    public triggerFileInput(): void {
        this.fileInput.nativeElement.click();
    }

    /**
     * Rotate image left (counter-clockwise)
     */
    public rotateLeft(): void {
        this.cropper?.rotate(-90);
    }

    /**
     * Rotate image right (clockwise)
     */
    public rotateRight(): void {
        this.cropper?.rotate(90);
    }

    /**
     * Flip image horizontally
     */
    public flipHorizontal(): void {
        const scaleX = this.cropper?.getData().scaleX ?? 1;
        this.cropper?.scaleX(-scaleX);
    }

    /**
     * Flip image vertically
     */
    public flipVertical(): void {
        const scaleY = this.cropper?.getData().scaleY ?? 1;
        this.cropper?.scaleY(-scaleY);
    }

    /**
     * Zoom in
     */
    public zoomIn(): void {
        this.cropper?.zoom(0.1);
    }

    /**
     * Zoom out
     */
    public zoomOut(): void {
        this.cropper?.zoom(-0.1);
    }

    /**
     * Reset the cropper to original state
     */
    public reset(): void {
        this.cropper?.reset();
    }

    /**
     * Clear the image and start over
     */
    public clearImage(): void {
        this.destroyCropper();
        this.imageSrc = null;
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
    }

    /**
     * Remove existing photo
     */
    public removePhoto(): void {
        const result: ProfilePhotoUploadDialogResult = {
            action: 'remove',
        };
        this.dialogRef.close(result);
    }

    /**
     * Select a profile color from the palette
     */
    public selectColor(color: string): void {
        this.selectedColor = color;
        this.isCustomColor = false;
        this.cpColor = color;
    }

    /**
     * Live preview as user interacts with the color picker
     */
    public onCpColorChange(color: string): void {
        this.cpColor = color;
        this.selectedColor = color;
        this.isCustomColor = true;
    }

    /**
     * Toggle the color picker popup open/closed
     */
    public toggleCp(): void {
        this.cpOpen = !this.cpOpen;
    }

    /**
     * Called when the color picker popup opens
     */
    public onCpOpen(): void {
        this.cpOpen = true;
    }

    /**
     * Called when the color picker popup closes (cancel)
     */
    public onCpClose(): void {
        this.cpOpen = false;
    }

    /**
     * Called when the user clicks OK in the color picker.
     * Adds the color as a swatch in the custom palette.
     */
    public onCpSelect(color: string): void {
        this.selectedColor = color;
        this.cpColor = color;

        // Add to custom colors if not already in any palette
        if (!this.customColors.includes(color) && !this.colorPalette.includes(color)) {
            this.customColors.push(color);
        }
        // Now it's a palette color, not a "custom preview" anymore
        this.isCustomColor = false;
        this.cpOpen = false;
    }

    /**
     * Called when the user clicks Cancel in the color picker.
     * Reverts to the previously selected palette color.
     */
    public onCpCancel(): void {
        // Revert: if there's no custom color pending, restore previous
        this.cpColor = this.selectedColor;
        this.isCustomColor =
            !this.colorPalette.includes(this.selectedColor) && !this.customColors.includes(this.selectedColor);
        this.cpOpen = false;
    }

    /**
     * Save the selected profile color (without a photo)
     */
    public saveColor(): void {
        const result: ProfilePhotoUploadDialogResult = {
            action: 'color',
            profileColor: this.selectedColor,
        };
        this.dialogRef.close(result);
    }

    /**
     * Cancel and close dialog
     */
    public cancel(): void {
        const result: ProfilePhotoUploadDialogResult = {
            action: 'cancel',
        };
        this.dialogRef.close(result);
    }

    /**
     * Save the cropped image
     */
    public save(): void {
        if (!this.cropper) {
            return;
        }

        this.isLoading = true;

        // Get cropped canvas at 500x500 for good quality
        const canvas = this.cropper.getCroppedCanvas({
            width: 500,
            height: 500,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high',
        });

        // Convert to blob
        canvas.toBlob(
            (blob) => {
                this.isLoading = false;
                if (blob) {
                    const result: ProfilePhotoUploadDialogResult = {
                        action: 'upload',
                        croppedImageBlob: blob,
                    };
                    this.dialogRef.close(result);
                }
            },
            'image/jpeg',
            0.9
        );
    }
}
